import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Alert, CircularProgress, Paper, Chip, List, ListItem, ListItemIcon, ListItemText,
  Button, Stack,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckIcon from "@mui/icons-material/Check";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api";

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(); } catch { return ""; } };

// Банер стану підписки за billing.banner / ok.
function StatusBanner({ b, t }) {
  if (!b) return null;
  const until = b.until ? fmtDate(b.until) : "";
  if (!b.ok) {
    return <Alert severity="error" sx={{ mb: 2 }}>{t("billing.suspended")}</Alert>;
  }
  if (b.banner === "past_due") {
    return <Alert severity="warning" sx={{ mb: 2 }}>{t("billing.pastDue", { date: until })}</Alert>;
  }
  if (b.banner === "trial_ending") {
    return <Alert severity="warning" sx={{ mb: 2 }}>{t("billing.trialEnding", { date: until })}</Alert>;
  }
  if (b.banner === "canceled_ending") {
    return <Alert severity="info" sx={{ mb: 2 }}>{t("billing.canceledEnding", { date: until })}</Alert>;
  }
  if (b.status === "trialing") {
    return <Alert severity="info" sx={{ mb: 2 }}>{t("billing.trial", { date: fmtDate(b.trialEnd) })}</Alert>;
  }
  if (b.status === "active") {
    return <Alert severity="success" sx={{ mb: 2 }}>{t("billing.active", { plan: b.plan?.name || "", date: b.currentPeriodEnd ? fmtDate(b.currentPeriodEnd) : "" })}</Alert>;
  }
  return null;
}

// Ліниве підвантаження Paddle.js (лише коли користувач іде платити).
let paddleReady = null;
const loadPaddle = (environment, clientToken) => {
  if (paddleReady) return paddleReady;
  paddleReady = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    s.onload = () => {
      try {
        if (environment === "sandbox") window.Paddle.Environment.set("sandbox");
        window.Paddle.Initialize({ token: clientToken });
        resolve(window.Paddle);
      } catch (e) { reject(e); }
    };
    s.onerror = () => reject(new Error("Paddle.js не завантажився"));
    document.head.appendChild(s);
  });
  return paddleReady;
};

export default function Billing() {
  const { t } = useTranslation();
  const { activeWorkspaceId, workspaceRole } = useAuth();
  const isOwner = workspaceRole === "owner";

  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [payMsg, setPayMsg] = useState("");

  // Оплата карткою: конфіг із бекенду (ціна/кількість рахує сервер) → overlay
  const payWithCard = async () => {
    setError("");
    setPayBusy(true);
    try {
      const cfg = await api.getPaddleCheckout(activeWorkspaceId);
      const paddle = await loadPaddle(cfg.environment, cfg.clientToken);
      paddle.Checkout.open({
        items: [{ priceId: cfg.priceId, quantity: cfg.quantity }],
        customData: cfg.customData,
        settings: { displayMode: "overlay" },
      });
      // Після оплати Paddle шле вебхук — статус підтягнеться; підкажемо і будемо
      // м'яко перепитувати бекенд, поки не стане active.
      setPayMsg(t("billing.paddleProcessing"));
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const bl = await api.getWorkspaceBilling(activeWorkspaceId);
          if (bl?.billing?.status === "active") {
            clearInterval(poll);
            setPayMsg("");
            setBilling(bl.billing);
          }
        } catch { /* ignore */ }
        if (tries > 40) clearInterval(poll); // ~2 хв
      }, 3000);
    } catch (e) {
      setError(e.message);
    }
    setPayBusy(false);
  };

  const openPortal = async () => {
    setError("");
    try {
      const { url } = await api.getPaddlePortal(activeWorkspaceId);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setError(e.message);
    }
  };

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    setError("");
    try {
      const [pl, bl] = await Promise.all([
        api.getBillingPlans(),
        api.getWorkspaceBilling(activeWorkspaceId),
      ]);
      setPlans(pl.plans || []);
      setBilling(bl.billing || null);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [activeWorkspaceId]);

  useEffect(() => { load(); }, [load]);

  if (!activeWorkspaceId) {
    return <Box sx={{ py: 4 }}><Typography color="text.secondary">{t("billing.noWorkspace")}</Typography></Box>;
  }

  const currentPlanId = billing?.plan?.id;

  return (
    <Box sx={{ py: 2, maxWidth: 860 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>{t("billing.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t("billing.subtitle")}</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <StatusBanner b={billing} t={t} />

          {payMsg && <Alert severity="info" sx={{ mb: 2 }}>{payMsg}</Alert>}

          {/* Оплата: карткою через Paddle (як налаштовано) або за рахунком-фактурою */}
          {isOwner && (
            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
              {billing?.provider === "paddle" && ["active", "past_due", "canceled"].includes(billing?.status) ? (
                <Button variant="outlined" startIcon={<CreditCardIcon />} onClick={openPortal}>
                  {t("billing.paddlePortal")}
                </Button>
              ) : (
                <Button variant="contained" startIcon={<CreditCardIcon />} onClick={payWithCard}
                  disabled={payBusy}>
                  {payBusy ? t("billing.paddleOpening") : t("billing.payByCard")}
                </Button>
              )}
            </Stack>
          )}

          <Alert severity="info" icon={<ReceiptLongIcon />} sx={{ mb: 2 }}>
            {t("billing.invoiceNote")}
          </Alert>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t("billing.plansRef")}
          </Typography>
          <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, mb: 2 }}>
            {plans.map((p) => {
              const isCurrent = p.id === currentPlanId;
              return (
                <Paper key={p.id} variant="outlined" sx={{
                  p: 2.5, position: "relative",
                  borderColor: isCurrent ? "primary.main" : "divider",
                  borderWidth: isCurrent ? 2 : 1,
                }}>
                  {isCurrent && (
                    <Chip size="small" color="primary" label={t("billing.current")}
                      sx={{ position: "absolute", top: 12, right: 12 }} />
                  )}
                  <Typography variant="h6" fontWeight={700}>{p.name}</Typography>
                  <Typography variant="h4" fontWeight={800} sx={{ my: 0.5 }}>
                    {p.price} <Box component="span" sx={{ fontSize: "0.9rem", fontWeight: 600, color: "text.secondary" }}>{p.currency}/{p.interval === "year" ? t("billing.year") : t("billing.month")}</Box>
                  </Typography>
                  <List dense sx={{ my: 1 }}>
                    {(p.features || []).map((f, i) => (
                      <ListItem key={i} disableGutters sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                        <ListItemText primary={f} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}
